import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, } from 'recharts';
import { Box, Typography } from '@weave-mui/material';
// Distinct, stable colors per task type so a given type reads consistently across all
// charts — cycles if there are more task types than colors (unlikely in practice).
const LINE_COLORS = ['#0052cc', '#36b37e', '#ff8b00', '#6554c0', '#de350b', '#00b8d9'];
/** Merges each series' independent points into one array of rows keyed by bucketStart —
 * the shape recharts' multi-series LineChart expects. `cell` computes the column(s) a single
 * point contributes to its row; the three modes below only differ in that computation. */
function buildRows(series, cell) {
    const buckets = new Map();
    for (const s of series) {
        for (const point of s.points) {
            const row = buckets.get(point.bucketStart) ?? { bucketStart: point.bucketStart };
            cell(s, point, row);
            buckets.set(point.bucketStart, row);
        }
    }
    return Array.from(buckets.values()).sort((a, b) => String(a.bucketStart).localeCompare(String(b.bucketStart)));
}
function mergeValueRows(series, divisor) {
    return buildRows(series, (s, point, row) => {
        row[s.taskType] = (point.value ?? 0) / divisor;
    });
}
/** Running sum of `value` per taskType across time-sorted buckets. Exported for direct testing
 * of the accumulation itself, since it's the one piece of new arithmetic in this file. */
export function mergeCumulativeRows(series, divisor) {
    const running = new Map();
    const sortedSeries = series.map((s) => ({ ...s, points: [...s.points].sort((a, b) => a.bucketStart.localeCompare(b.bucketStart)) }));
    return buildRows(sortedSeries, (s, point, row) => {
        const sum = (running.get(s.taskType) ?? 0) + (point.value ?? 0);
        running.set(s.taskType, sum);
        row[s.taskType] = sum / divisor;
    });
}
/** Two columns per taskType (`<taskType> p75`, `<taskType> max`) so both lines render. */
function mergePercentileRows(series, divisor) {
    return buildRows(series, (s, point, row) => {
        row[`${s.taskType} p75`] = (point.p75 ?? 0) / divisor;
        row[`${s.taskType} max`] = (point.max ?? 0) / divisor;
    });
}
export default function TrendChart({ title, series, valueLabel, mode = 'value', unitDivisor = 1 }) {
    const hasData = series.some((s) => s.points.length > 0);
    const rows = mode === 'cumulative'
        ? mergeCumulativeRows(series, unitDivisor)
        : mode === 'percentile'
            ? mergePercentileRows(series, unitDivisor)
            : mergeValueRows(series, unitDivisor);
    return (_jsxs(Box, { sx: { bgcolor: 'background.paper', borderRadius: 2, p: 2, border: '1px solid #dfe1e6', minHeight: 320 }, children: [_jsx(Typography, { component: "h2", sx: { fontFamily: '"DM Sans", sans-serif', fontWeight: 500, fontSize: '1rem', color: '#172b4d', mb: 2 }, children: title }), !hasData ? (_jsx(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }, children: _jsx(Typography, { color: "text.secondary", children: "No data for this period" }) })) : (_jsx(ResponsiveContainer, { width: "100%", height: 240, children: _jsxs(LineChart, { data: rows, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3" }), _jsx(XAxis, { dataKey: "bucketStart", tick: { fontSize: 11 } }), _jsx(YAxis, { tick: { fontSize: 11 }, label: { value: valueLabel, angle: -90, position: 'insideLeft', fontSize: 11 } }), _jsx(Tooltip, {}), _jsx(Legend, {}), mode === 'percentile'
                            ? series.map((s, i) => (_jsxs(Fragment, { children: [_jsx(Line, { type: "monotone", dataKey: `${s.taskType} p75`, name: `${s.taskType} p75`, stroke: LINE_COLORS[i % LINE_COLORS.length], connectNulls: true, dot: false }), _jsx(Line, { type: "monotone", dataKey: `${s.taskType} max`, name: `${s.taskType} max`, stroke: LINE_COLORS[i % LINE_COLORS.length], strokeDasharray: "4 3", connectNulls: true, dot: false })] }, s.taskType)))
                            : series.map((s, i) => (_jsx(Line, { type: "monotone", dataKey: s.taskType, name: s.taskType, stroke: LINE_COLORS[i % LINE_COLORS.length], connectNulls: true, dot: false }, s.taskType)))] }) }))] }));
}
